import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Card, CardContent, CardHeader, Divider, Grid, Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useParams } from 'react-router-dom';

import { MakeChildOfStrategy } from './components/MakeChildOfStrategy';
import { ManageStrategySection } from './components/ManageStrategySection';
import { PublishBorrSection } from './components/PublishBorrSection';
import { PublishEvaSection } from './components/PublishEvaSection';
import { StrategyDataSection } from './components/StrategyDataSection';
import { StrategySignalsSection } from './components/StrategySignalsSection';
import { AggregateStrategies$, aggregateStrategiesApi } from '../../../effector/aggregateStrategies';
import { Option } from '../../../types/option';

export const StrategyDetails: React.FC = () => {
  const { strategyId } = useParams<{ strategyId: string }>();

  const aggregateStrategiesStore = useUnit(AggregateStrategies$);
  const strategy = aggregateStrategiesStore.strategies.find((s) => s.id === strategyId);

  const parentStrategy = !strategy?.parentId
    ? undefined
    : aggregateStrategiesStore.strategies.find((s) => s.id === strategy?.parentId);

  const parentStrategies: Option[] = useMemo(() => {
    if (!strategy || parentStrategy) return [];

    return aggregateStrategiesStore.strategies
      .filter((s) => s.parentId === null && s.id !== strategy.id)
      .map((s) => ({ id: s.id, name: s.name }));
  }, [aggregateStrategiesStore.strategies, strategy, parentStrategy]);

  const loadStrategies = useCallback(() => {
    aggregateStrategiesApi.get('');
  }, []);

  useEffect(() => {
    if (aggregateStrategiesStore.strategies.length === 0 && strategyId) {
      loadStrategies();
    }
  }, [strategyId, aggregateStrategiesStore, loadStrategies]);

  if (!strategyId || strategyId.length === 0) {
    return (
      <Box role="alert" aria-live="polite" tabIndex={0}>
        <Typography>Не выбрана стратегия</Typography>
      </Box>
    );
  }

  if (!strategy) {
    return (
      <Box role="alert" aria-live="polite" tabIndex={0}>
        <Typography>Стратегия не найдена</Typography>
      </Box>
    );
  }

  const strategyTypeInfo: string =
    strategy.autofollow && strategy.autoconsult
      ? 'Автоследование, Автоконсультирование'
      : strategy.autofollow
      ? 'Автоследование'
      : 'Автоконсультирование';

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h1">
        {strategy.name}
      </Typography>

      <Grid container spacing={2} maxWidth="100%">
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Информация о стратегии" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Идентификатор стратегии</Typography>
                  <Typography>{String(strategy.id)}</Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Время создания</Typography>
                  <Typography>{String(strategy.creationDate)}</Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Типы следования</Typography>
                  <Typography>{strategyTypeInfo || 'Не задано'}</Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Ссылка</Typography>
                  <Typography>
                    <a href={strategy.friendlyUrl} target="_blank" rel="noopener noreferrer">
                      {strategy.friendlyUrl}
                    </a>
                  </Typography>
                </Stack>
                {parentStrategy && (
                  <Stack spacing={0.5}>
                    <Typography color="text.secondary">Родительская стратегия</Typography>
                    <Typography>{parentStrategy.name}</Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <ManageStrategySection strategyId={strategyId as string} initialFrozen={strategy.isFrozen ?? false} />
        </Grid>

        <Grid item xs={12} md={6}>
          <MakeChildOfStrategy
            strategyId={strategyId as string}
            options={parentStrategies}
            parentStrategy={parentStrategy}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PublishEvaSection
            strategyId={strategyId as string}
            lastUpdated={strategy.evaParams?.evaUpdatedAt ?? 'Никогда'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <PublishBorrSection
            strategyId={strategyId as string}
            updatedAt={strategy.borrPublication?.updatedAt ?? 'Никогда'}
            success={strategy.borrPublication?.success}
            errorDescription={strategy.borrPublication?.description}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <StrategyDataSection />
        </Grid>
      </Grid>

      <StrategySignalsSection />
    </Stack>
  );
};
